import { useState, useEffect, useCallback, useRef } from "react";
import api, { formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Trash2, QrCode, UserPlus, Building, Loader2, X, Pencil, Camera, IdCard, User, Home } from "lucide-react";
import IdBadge from "@/components/Badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";

export default function Employees() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [depName, setDepName] = useState("");
  const [qr, setQr] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", department_id: "", position: "", pin: "", schedule_id: "", allow_remote: false });
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [badge, setBadge] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const fileRefs = useRef({});

  const load = useCallback(async () => {
    const [e, d, s] = await Promise.all([api.get("/employees"), api.get("/departments"), api.get("/schedules")]);
    setEmployees(e.data); setDepartments(d.data); setSchedules(s.data); setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addEmployee = async () => {
    if (!form.name || !form.email || !form.password || !form.pin) return toast.error("Completa los campos obligatorios");
    setSaving(true);
    try {
      await api.post("/employees", { ...form, department_id: form.department_id || null, schedule_id: form.schedule_id || null });
      toast.success("Empleado creado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", department_id: "", position: "", pin: "", schedule_id: "", allow_remote: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este empleado?")) return;
    await api.delete(`/employees/${id}`);
    toast.success("Empleado eliminado"); load();
  };

  const addDep = async () => {
    if (!depName.trim()) return;
    await api.post("/departments", { name: depName.trim() });
    setDepName(""); toast.success("Departamento creado"); load();
  };
  const removeDep = async (id) => { await api.delete(`/departments/${id}`); load(); };

  const showQr = async (emp) => {
    const { data } = await api.get(`/employees/${emp.id}/qr`);
    setQr({ ...data, name: emp.name });
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setEditForm({ name: emp.name, email: emp.email, position: emp.position || "", pin: emp.pin, department_id: emp.department_id || "", status: emp.status, hire_date: emp.hire_date || "", schedule_id: emp.schedule_id || "", allow_remote: !!emp.allow_remote });
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = { ...editForm };
      if (!payload.department_id) delete payload.department_id;
      payload.schedule_id = editForm.schedule_id || null;
      await api.patch(`/employees/${editing.id}`, payload);
      toast.success("Empleado actualizado");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const uploadPhoto = async (empId, file) => {
    if (!file) return;
    setUploadingId(empId);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/employees/${empId}/photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto actualizada");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploadingId(null); }
  };

  const showBadge = async (emp) => {
    const { data } = await api.get(`/employees/${emp.id}/badge`);
    setBadge(data);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Empleados</h1>
          <p className="text-slate-500 text-sm mt-1">Gestiona tu plantilla, PINs de quiosco y departamentos.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="brand-btn" data-testid="add-employee-btn"><UserPlus className="h-4 w-4 mr-2" /> Nuevo empleado</Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle className="font-heading">Nuevo empleado</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nombre completo *</Label><Input data-testid="emp-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Email *</Label><Input data-testid="emp-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1" /></div>
                <div><Label>Contraseña *</Label><Input data-testid="emp-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>PIN quiosco *</Label><Input data-testid="emp-pin" value={form.pin} onChange={(e) => setForm({ ...form, pin: e.target.value })} placeholder="4 dígitos" className="mt-1" /></div>
                <div><Label>Puesto</Label><Input data-testid="emp-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} className="mt-1" /></div>
              </div>
              <div>
                <Label>Departamento</Label>
                <Select value={form.department_id} onValueChange={(v) => setForm({ ...form, department_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="emp-dept"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horario asignado</Label>
                <Select value={form.schedule_id} onValueChange={(v) => setForm({ ...form, schedule_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="emp-schedule"><SelectValue placeholder="Hereda del departamento" /></SelectTrigger>
                  <SelectContent>{schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
                <div className="flex items-center gap-2 text-sm text-slate-700"><Home className="h-4 w-4 text-slate-400" /> Permitir teletrabajo</div>
                <Switch data-testid="emp-allow-remote" checked={form.allow_remote} onCheckedChange={(v) => setForm({ ...form, allow_remote: v })} />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={addEmployee} disabled={saving} className="brand-btn" data-testid="save-employee-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear empleado"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Departments */}
      <Card className="p-5 border-slate-200 shadow-none">
        <div className="flex items-center gap-2 mb-3"><Building className="h-4 w-4 text-slate-400" /><h3 className="font-heading font-semibold">Departamentos</h3></div>
        <div className="flex flex-wrap gap-2 items-center">
          {departments.map((d) => (
            <Badge key={d.id} variant="secondary" className="pl-3 pr-1 py-1.5 gap-1 text-sm">
              {d.name} <span className="text-slate-400">({d.employee_count})</span>
              <button onClick={() => removeDep(d.id)} className="ml-1 hover:text-red-500" data-testid={`del-dept-${d.id}`}><X className="h-3.5 w-3.5" /></button>
            </Badge>
          ))}
          <div className="flex gap-2 items-center">
            <Input value={depName} onChange={(e) => setDepName(e.target.value)} placeholder="Nuevo departamento" className="h-9 w-44" data-testid="dept-name-input" onKeyDown={(e) => e.key === "Enter" && addDep()} />
            <Button size="sm" variant="outline" onClick={addDep} data-testid="add-dept-btn"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 shadow-none overflow-hidden">
        {loading ? (
          <div className="h-48 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-300" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Empleado</TableHead><TableHead>Departamento</TableHead>
                <TableHead>Horario</TableHead><TableHead>PIN</TableHead>
                <TableHead>Estado</TableHead><TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((e) => (
                <TableRow key={e.id} className="group" data-testid={`emp-row-${e.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="h-9 w-9">
                        {e.photo_path ? <AvatarImage src={fileUrl(e.photo_path)} alt={e.name} /> : null}
                        <AvatarFallback className="bg-slate-100 text-slate-500 text-xs">{e.name?.[0]?.toUpperCase()}</AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium text-slate-800">{e.name}</div>
                        <div className="text-xs text-slate-400">{e.email}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-slate-600">{e.department_name}</TableCell>
                  <TableCell className="text-slate-600">
                    <div className="text-sm">{e.schedule_name || <span className="text-slate-300">—</span>}</div>
                    {e.allow_remote && <Badge variant="outline" className="mt-0.5 text-violet-600 border-violet-200 text-[10px] gap-1"><Home className="h-2.5 w-2.5" /> Teletrabajo</Badge>}
                  </TableCell>
                  <TableCell><code className="px-2 py-0.5 rounded bg-slate-100 text-slate-700 text-xs">{e.pin}</code></TableCell>
                  <TableCell><Badge variant={e.status === "active" ? "secondary" : "outline"} className="capitalize">{e.status === "active" ? "Activo" : "Inactivo"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                      <input type="file" accept="image/*" hidden ref={(el) => (fileRefs.current[e.id] = el)} onChange={(ev) => uploadPhoto(e.id, ev.target.files[0])} data-testid={`photo-input-${e.id}`} />
                      <Button size="icon" variant="ghost" onClick={() => fileRefs.current[e.id]?.click()} disabled={uploadingId === e.id} title="Subir foto" data-testid={`photo-${e.id}`}>{uploadingId === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Camera className="h-4 w-4" />}</Button>
                      <Button size="icon" variant="ghost" onClick={() => showBadge(e)} title="Carnet identificativo" data-testid={`badge-${e.id}`}><IdCard className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(e)} title="Editar" data-testid={`edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => showQr(e)} title="QR fichaje" data-testid={`qr-${e.id}`}><QrCode className="h-4 w-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => remove(e.id)} className="hover:text-red-500" title="Eliminar" data-testid={`del-emp-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!employees.length && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-10">No hay empleados todavía.</TableCell></TableRow>}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Editar empleado</DialogTitle></DialogHeader>
          <div className="flex justify-center mb-2">
            <Avatar className="h-20 w-20">
              {editing?.photo_path ? <AvatarImage src={fileUrl(editing.photo_path)} /> : null}
              <AvatarFallback className="bg-slate-100"><User className="h-8 w-8 text-slate-300" /></AvatarFallback>
            </Avatar>
          </div>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={editForm.name || ""} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="mt-1" data-testid="edit-name" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Email</Label><Input type="email" value={editForm.email || ""} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="mt-1" data-testid="edit-email" /></div>
              <div><Label>PIN</Label><Input value={editForm.pin || ""} onChange={(e) => setEditForm({ ...editForm, pin: e.target.value })} className="mt-1" data-testid="edit-pin" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Puesto</Label><Input value={editForm.position || ""} onChange={(e) => setEditForm({ ...editForm, position: e.target.value })} className="mt-1" data-testid="edit-position" /></div>
              <div><Label>Fecha de alta</Label><Input type="date" value={editForm.hire_date || ""} onChange={(e) => setEditForm({ ...editForm, hire_date: e.target.value })} className="mt-1" data-testid="edit-hiredate" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Departamento</Label>
                <Select value={editForm.department_id || ""} onValueChange={(v) => setEditForm({ ...editForm, department_id: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-dept"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Estado</Label>
                <Select value={editForm.status || "active"} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger className="mt-1" data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Activo</SelectItem><SelectItem value="inactive">Inactivo</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Horario asignado</Label>
              <Select value={editForm.schedule_id || ""} onValueChange={(v) => setEditForm({ ...editForm, schedule_id: v })}>
                <SelectTrigger className="mt-1" data-testid="edit-schedule"><SelectValue placeholder="Hereda del departamento" /></SelectTrigger>
                <SelectContent>{schedules.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div className="flex items-center gap-2 text-sm text-slate-700"><Home className="h-4 w-4 text-slate-400" /> Permitir teletrabajo</div>
              <Switch data-testid="edit-allow-remote" checked={!!editForm.allow_remote} onCheckedChange={(v) => setEditForm({ ...editForm, allow_remote: v })} />
            </div>
          </div>
          <DialogFooter><Button onClick={saveEdit} disabled={saving} className="brand-btn" data-testid="save-edit-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Guardar cambios"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!badge} onOpenChange={() => setBadge(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Carnet identificativo</DialogTitle></DialogHeader>
          <IdBadge data={badge} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!qr} onOpenChange={() => setQr(null)}>
        <DialogContent className="sm:max-w-xs text-center">
          <DialogHeader><DialogTitle className="font-heading">{qr?.name}</DialogTitle></DialogHeader>
          {qr && <img src={qr.qr} alt="QR" className="mx-auto rounded-xl border border-slate-200" />}
          <p className="text-sm text-slate-500">PIN de fichaje: <code className="font-bold">{qr?.pin}</code></p>
          <p className="text-xs text-slate-400">Escanea o introduce este PIN en el modo quiosco.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
